import React, { useState } from "react";
import {
  FaTimes,
  FaEdit,
  FaCheckCircle,
  FaClock,
  FaPhone,
  FaCommentAlt,
} from "react-icons/fa";

const IssueStatusUpdateModal = ({ show, issue, onClose, onUpdate }) => {
  const [formData, setFormData] = useState({
    status: issue?.status || "",
    contactNotes: "",
    resolutionNotes: "",
  });
  const [loading, setLoading] = useState(false);

  // Reset form when issue changes
  React.useEffect(() => {
    if (issue) {
      setFormData({
        status: issue.status || "",
        contactNotes: issue.contactNotes || "",
        resolutionNotes: issue.resolutionNotes || "",
      });
    }
  }, [issue]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation
    if (!formData.status) {
      alert("Please select a status");
      return;
    }

    if (formData.status === "Resolved" && !formData.resolutionNotes) {
      alert("Resolution notes are required when marking as resolved");
      return;
    }

    setLoading(true);
    try {
      await onUpdate(issue._id, formData);
      handleClose();
    } catch (error) {
      console.error("Status update error:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({
      status: issue?.status || "",
      contactNotes: "",
      resolutionNotes: "",
    });
    onClose();
  };

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  if (!show || !issue) return null;

  const statusOptions = [
    {
      value: "Pending",
      label: "Pending",
      icon: FaClock,
      color: "text-yellow-600",
    },
    {
      value: "In progress",
      label: "In Progress",
      icon: FaClock,
      color: "text-blue-600",
    },
    {
      value: "Resolved",
      label: "Resolved",
      icon: FaCheckCircle,
      color: "text-green-600",
    },
  ];

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-10 mx-auto p-6 border max-w-lg shadow-lg rounded-lg bg-white">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center">
            <FaEdit className="text-blue-600 mr-2" />
            <h3 className="text-lg font-semibold text-gray-900">
              Update Issue Status
            </h3>
          </div>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <FaTimes />
          </button>
        </div>

        {/* Issue Summary */}
        <div className="mb-4 p-3 bg-gray-50 rounded-lg">
          <h4 className="font-medium text-gray-900 mb-1">{issue.title}</h4>
          <p className="text-sm text-gray-600">{issue.issueType}</p>
          <p className="text-xs text-gray-500 mt-1">
            <FaPhone className="inline mr-1" />
            {issue.phone} • {issue.role}
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Status Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Update Status <span className="text-red-500">*</span>
            </label>
            <div className="space-y-2">
              {statusOptions.map((option) => {
                const IconComponent = option.icon;
                return (
                  <label
                    key={option.value}
                    className="flex items-center cursor-pointer"
                  >
                    <input
                      type="radio"
                      name="status"
                      value={option.value}
                      checked={formData.status === option.value}
                      onChange={(e) =>
                        handleInputChange("status", e.target.value)
                      }
                      className="mr-3"
                    />
                    <IconComponent className={`${option.color} mr-2`} />
                    <span className="font-medium">{option.label}</span>
                  </label>
                );
              })}
            </div>
          </div>

          {/* Contact Notes */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <FaPhone className="inline mr-1" />
              Customer Contact Notes
            </label>
            <textarea
              value={formData.contactNotes}
              onChange={(e) =>
                handleInputChange("contactNotes", e.target.value)
              }
              placeholder="Add notes about customer interaction, phone calls, etc..."
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              rows={3}
            />
            <p className="text-xs text-gray-500 mt-1">
              Record details about when and how you contacted the customer
            </p>
          </div>

          {/* Resolution Notes - Show only when status is Resolved */}
          {formData.status === "Resolved" && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <FaCommentAlt className="inline mr-1" />
                Resolution Notes <span className="text-red-500">*</span>
              </label>
              <textarea
                value={formData.resolutionNotes}
                onChange={(e) =>
                  handleInputChange("resolutionNotes", e.target.value)
                }
                placeholder="Describe how the issue was resolved, what steps were taken, etc..."
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                rows={4}
                required
              />
              <p className="text-xs text-gray-500 mt-1">
                Required: Explain what was done to resolve the customer's issue
              </p>
            </div>
          )}

          {/* Current Status Info */}
          <div className="p-3 bg-blue-50 rounded-lg">
            <p className="text-sm text-blue-700">
              <strong>Current Status:</strong> {issue.status}
            </p>
            {issue.assignedTo && (
              <p className="text-sm text-blue-600">
                <strong>Assigned to:</strong> {issue.assignedTo.fullName} (
                {issue.assignedTo.role})
              </p>
            )}
          </div>

          {/* Actions */}
          <div className="flex justify-end space-x-3 pt-4 border-t">
            <button
              type="button"
              onClick={handleClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-500"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? "Updating..." : "Update Status"}
            </button>
          </div>
        </form>

        {/* Status Change Guidelines */}
        <div className="mt-4 p-3 bg-yellow-50 rounded-lg">
          <p className="text-xs text-yellow-700">
            <strong>Guidelines:</strong>
          </p>
          <ul className="text-xs text-yellow-600 mt-1 space-y-1">
            <li>• Set to "In Progress" when actively working on the issue</li>
            <li>
              • Always add contact notes when communicating with customers
            </li>
            <li>
              • Mark as "Resolved" only after confirming the solution worked
            </li>
            <li>• Include detailed resolution notes for future reference</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default IssueStatusUpdateModal;
